import React, { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";

export const FU_QUICK = [["+2 days", 2], ["+1 week", 7], ["+2 weeks", 14], ["+1 month", 30]];
export const addDaysISO = (d) => { const x = new Date(); x.setDate(x.getDate() + d); return x.toISOString().slice(0, 10); };
export const isTodayOrPast = (iso) => !!iso && iso.slice(0, 10) <= new Date().toISOString().slice(0, 10);

// Quick follow-up picker (+2d · +1w · +2w · +1m · pick a date). Optional Next Action field.
export function FollowupPicker({ value, onChange, idPrefix = "fu", withNextAction = false, nextAction, onNextAction }) {
  return (
    <div className="flex flex-wrap items-center gap-1.5" data-testid={`${idPrefix}-picker`}>
      {FU_QUICK.map(([l, d]) => (
        <button key={d} type="button" onClick={() => onChange(addDaysISO(d))} data-testid={`${idPrefix}-quick-${d}`}
          className={`px-2 py-0.5 rounded-full text-[11px] border ${value === addDaysISO(d) ? "bg-[#FF4802] text-white border-[#FF4802]" : "bg-white text-[#6B7280] border-[#E2E2E2] hover:border-[#FF6F38]"}`}>{l}</button>
      ))}
      <input type="date" value={value || ""} min={addDaysISO(0)} onChange={(e) => onChange(e.target.value)} data-testid={`${idPrefix}-date`}
        className="text-[11px] border border-[#E2E2E2] rounded-md px-1.5 py-0.5 bg-white" title="Pick a date" />
      {withNextAction && (
        <input value={nextAction || ""} onChange={(e) => onNextAction(e.target.value)} placeholder="Next action (required)" data-testid={`${idPrefix}-next-action`}
          className="text-[11px] border border-[#E2E2E2] rounded-md px-2 py-1 bg-white w-56 focus:outline-none focus:ring-2 focus:ring-[#FF4802]/40" />
      )}
    </div>
  );
}

// One-tap row actions for a leaking consulted lead: quick picks save immediately; Mark Lost needs a reason.
export function LeakRowActions({ cs, lostReasons, onDone }) {
  const [busy, setBusy] = useState(false);
  const [lostOpen, setLostOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [pickDate, setPickDate] = useState("");
  const save = async (date) => {
    if (!date) return;
    setBusy(true);
    try { await api.post(`/cases/${cs.record_id}/followup`, { follow_up_date: date }); toast.success(`Follow-up set · ${cs.case_id}`); onDone(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not set follow-up"); }
    finally { setBusy(false); }
  };
  const markLost = async () => {
    if (!reason) { toast.error("Lost reason is required"); return; }
    setBusy(true);
    try { await api.post(`/cases/${cs.record_id}/stage`, { stage: "Lost", lost_reason: reason }); toast.success(`${cs.case_id} marked Lost`); onDone(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not mark Lost"); }
    finally { setBusy(false); }
  };
  return (
    <div className="flex flex-wrap items-center gap-1.5" data-testid={`leak-actions-${cs.case_id}`}>
      {FU_QUICK.map(([l, d]) => (
        <button key={d} disabled={busy} onClick={() => save(addDaysISO(d))} data-testid={`leak-fu-${d}-${cs.case_id}`}
          className="px-2 py-0.5 rounded-full text-[11px] border bg-white text-[#6B7280] border-[#E2E2E2] hover:border-[#FF4802] hover:text-[#FF4802] disabled:opacity-50">{l}</button>
      ))}
      <input type="date" value={pickDate} min={addDaysISO(0)} onChange={(e) => { setPickDate(e.target.value); save(e.target.value); }} data-testid={`leak-fu-date-${cs.case_id}`}
        className="text-[11px] border border-[#E2E2E2] rounded-md px-1.5 py-0.5 bg-white" title="Pick a date" />
      {!lostOpen ? (
        <button onClick={() => setLostOpen(true)} data-testid={`leak-lost-${cs.case_id}`} className="px-2 py-0.5 rounded-full text-[11px] border border-[#CA1212]/40 text-[#CA1212] hover:bg-[#CA1212]/5">Mark Lost</button>
      ) : (
        <span className="flex items-center gap-1">
          <select value={reason} onChange={(e) => setReason(e.target.value)} data-testid={`leak-lost-reason-${cs.case_id}`} className="text-[11px] border border-[#E2E2E2] rounded-md px-1.5 py-0.5 bg-white">
            <option value="">Lost reason…</option>
            {(lostReasons || []).map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
          <button onClick={markLost} disabled={busy} data-testid={`leak-lost-confirm-${cs.case_id}`} className="px-2 py-0.5 rounded-full text-[11px] bg-[#CA1212] text-white disabled:opacity-50">Confirm</button>
          <button onClick={() => setLostOpen(false)} className="text-[11px] text-[#6B7280] hover:underline">Cancel</button>
        </span>
      )}
    </div>
  );
}
